import { defineComponent, ref, computed, reactive, toRef, watch, nextTick, onMounted, resolveComponent, openBlock, createElementBlock, normalizeClass, createElementVNode, Fragment, renderList, createBlock, withModifiers, withCtx, renderSlot, createTextVNode, toDisplayString, createVNode, createCommentVNode, withDirectives, vShow } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { r as re } from '../OrAutocompleteV3-508d99c0.js';
import { OrIconV3 as script$1 } from '../or-icon-v3/index.js';
import { OrMenuV3 as script$2 } from '../or-menu-v3/index.js';
import { OrMenuItemV3 as script$3 } from '../or-menu-item-v3/index.js';
import { OrTabV3 as script$4 } from '../or-tab-v3/index.js';
import '@onereach/styles/screens.json';
import '@onereach/styles/tailwind.config.json';
import 'lodash/get';
import 'lodash/isElement';
import '../or-error-v3/index.js';
import '../or-hint-v3/index.js';
import '../or-icon-button-v3/index.js';
import '../or-tooltip-v3/index.js';
import '../or-popover-v3/index.js';
import '@floating-ui/dom';
import '../or-bottom-sheet-v3/index.js';
import '../or-overlay-v3/index.js';
import '../or-teleport-v3/index.js';
import '../or-input-box-v3/index.js';
import '../or-input-v3/index.js';
import '../OrInlineInputV3-46210775.js';
import '../or-inline-input-box-v3/index.js';
import '../or-text-v3/index.js';
import '../or-label-v3/index.js';
import '../or-loader-v3/index.js';
import '../or-tag-input-v3/index.js';
import '../or-tags-v3/index.js';
import '../or-button-v3/index.js';
import '../or-tag-v3/index.js';

var TabsVariant;
(function (TabsVariant) {
  TabsVariant["Default"] = "default";
  TabsVariant["Fitted"] = "fitted";
})(TabsVariant || (TabsVariant = {}));

const TabsContainer = [
// Layout
'layout-column', 'grow',
// Box
'h-full',
// Spacing
'gap-lg'];
const Tabs = [
// Layout
'grid grid-flow-col items-center shrink-0',
// Theme
'theme-border-outline border-b-1', 'dark:theme-border-outline'];
const TabsVariants = {
  'default': [
  // Layout
  'auto-cols-max',
  // Spacing
  'gap-lg'],
  'fitted': [
  // Layout
  'auto-cols-auto',
  // Spacing (children)
  'children:px-lg']
};
const TabsContent = [
// Layout
'layout-column', 'grow',
// Box
'h-full',
// Overflow
'overflow-auto'];

var script = defineComponent({
  name: 'OrTabs',
  components: {
    OrIcon: script$1,
    OrMenu: script$2,
    OrMenuItem: script$3,
    OrTab: script$4
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: [String, Number],
      default: undefined
    },
    items: {
      type: Array,
      required: true
    },
    variant: {
      type: String,
      default: () => TabsVariant.Default
    },
    keepAlive: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  expose: ['root'],
  setup(props, context) {
    // Refs
    const root = ref();
    const overflowTab = ref();
    const overflowTabRoot = computed(() => {
      var _a;
      return (_a = overflowTab.value) === null || _a === void 0 ? void 0 : _a.root;
    });
    const overflowMenu = ref();
    const overflowMenuRoot = computed(() => {
      var _a;
      return (_a = overflowMenu.value) === null || _a === void 0 ? void 0 : _a.root;
    });
    // Styles
    const containerStyles = computed(() => [...TabsContainer]);
    const rootStyles = computed(() => ['or-tabs-v3', ...Tabs, ...TabsVariants[props.variant]]);
    const contentStyles = computed(() => [...TabsContent]);
    // State
    const tabs = reactive({
      normal: [],
      hidden: []
    });
    const proxyModelValue = re(toRef(props, 'modelValue'), context.emit);
    const activeTab = computed(() => props.items.find(({
      value
    }) => value === proxyModelValue.value));
    // Methods
    function selectTab(tab) {
      if (!tab.disabled) {
        proxyModelValue.value = tab.value;
      }
    }
    function rearrangeTabs(items, activeTab, rootElement) {
      const {
        children
      } = rootElement;
      const nextNormalTabs = [...items];
      const nextHiddenTabs = [];
      let currentWidth = rootElement.scrollWidth;
      [...children].reverse().forEach(element => {
        if (currentWidth > rootElement.clientWidth) {
          nextHiddenTabs.unshift(nextNormalTabs.pop());
          currentWidth -= element.clientWidth + (props.variant === TabsVariant.Default ? 16 : 0);
        }
      });
      if (nextHiddenTabs.includes(activeTab)) {
        nextHiddenTabs.unshift(nextNormalTabs.pop());
        nextNormalTabs.push(...nextHiddenTabs.splice(nextHiddenTabs.indexOf(activeTab), 1));
      }
      return {
        normal: nextNormalTabs,
        hidden: nextHiddenTabs
      };
    }
    function isTabRendered(value) {
      return props.keepAlive || proxyModelValue.value === value;
    }
    // Effects
    watch([() => props.items, activeTab, root], ([items, activeTab, rootElement]) => {
      tabs.normal = items;
      nextTick(() => {
        Object.assign(tabs, rearrangeTabs(items, activeTab, rootElement));
      });
    });
    // #region Resize
    useResizeObserver(root, () => {
      tabs.normal = props.items;
      nextTick(() => {
        Object.assign(tabs, rearrangeTabs(props.items, activeTab.value, root.value));
      });
    });
    // #endregion
    onMounted(() => {
      var _a;
      if (!proxyModelValue.value) {
        proxyModelValue.value = (_a = props.items[0]) === null || _a === void 0 ? void 0 : _a.value;
      }
    });
    return {
      root,
      overflowTab,
      overflowTabRoot,
      overflowMenu,
      overflowMenuRoot,
      containerStyles,
      rootStyles,
      contentStyles,
      tabs,
      proxyModelValue,
      activeTab,
      selectTab,
      isTabRendered
    };
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrTab = resolveComponent("OrTab");
  const _component_OrIcon = resolveComponent("OrIcon");
  const _component_OrMenuItem = resolveComponent("OrMenuItem");
  const _component_OrMenu = resolveComponent("OrMenu");
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(_ctx.containerStyles)
  }, [createElementVNode("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [(openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.tabs.normal, tab => {
    return openBlock(), createBlock(_component_OrTab, {
      key: tab.value,
      selected: tab.value === _ctx.proxyModelValue,
      invalid: tab.invalid,
      disabled: tab.disabled,
      onClick: withModifiers($event => _ctx.selectTab(tab), ["stop"])
    }, {
      default: withCtx(() => [renderSlot(_ctx.$slots, "tab", {
        tab: tab,
        activeTab: _ctx.activeTab
      }, () => [createTextVNode(toDisplayString(tab.label), 1 /* TEXT */)])]),
      _: 2 /* DYNAMIC */
    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["selected", "invalid", "disabled", "onClick"]);
  }), 128 /* KEYED_FRAGMENT */)), _ctx.tabs.hidden.length > 0 ? (openBlock(), createElementBlock(Fragment, {
    key: 0
  }, [createVNode(_component_OrTab, {
    ref: 'overflowTab',
    onClick: _cache[0] || (_cache[0] = withModifiers($event => _ctx.overflowMenu && _ctx.overflowMenu.toggle(), ["stop"]))
  }, {
    default: withCtx(() => [createVNode(_component_OrIcon, {
      icon: 'more_horiz',
      variant: 'filled-bold'
    })]),
    _: 1 /* STABLE */
  }), createVNode(_component_OrMenu, {
    ref: 'overflowMenu',
    trigger: _ctx.overflowTabRoot
  }, {
    default: withCtx(() => [(openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.tabs.hidden, tab => {
      return openBlock(), createBlock(_component_OrMenuItem, {
        key: tab.value,
        disabled: tab.disabled,
        onClick: withModifiers($event => {
          _ctx.selectTab(tab);
          _ctx.overflowMenu && _ctx.overflowMenu.close();
        }, ["stop"])
      }, {
        default: withCtx(() => [createTextVNode(toDisplayString(tab.label), 1 /* TEXT */)]),
        _: 2 /* DYNAMIC */
      }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["disabled", "onClick"]);
    }), 128 /* KEYED_FRAGMENT */))]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["trigger"])], 64 /* STABLE_FRAGMENT */)) : createCommentVNode("v-if", true)], 2 /* CLASS */), createElementVNode("div", {
    class: normalizeClass(_ctx.contentStyles)
  }, [(openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.items, tab => {
    return openBlock(), createElementBlock(Fragment, null, [_ctx.isTabRendered(tab.value) ? withDirectives((openBlock(), createElementBlock("div", {
      key: tab.value,
      class: "contents"
    }, [renderSlot(_ctx.$slots, String(tab.value))])), [[vShow, String(_ctx.proxyModelValue) === String(tab.value)]]) : createCommentVNode("v-if", true)], 64 /* STABLE_FRAGMENT */);
  }), 256 /* UNKEYED_FRAGMENT */))], 2 /* CLASS */)], 2 /* CLASS */);
}

script.render = render;
script.__file = "src/components/or-tabs-v3/OrTabs.vue";

export { script as OrTabsV3, TabsVariant };
